import { CalendarEvent } from "vuetify/lib/components/VCalendar/types.mjs";
import { useAuthStore } from "@/stores/auth";
import { useUser } from "@/composables/useUser";
import { useEnv } from "../useEnv";
import { useNotificationStore } from "@/stores/notification";
import type { MemberOrderSession } from "@/composables/shop/useMemberOrder";
import { useClassUnifiedService } from "../../../cbi-generator/frontend/composables/useClassUnifiedFrontendService";
import type { ClassEligibility } from "../../../cbi-generator/generated/dto/ClassEligibility";
import type { ClassEnrollmentOverrides } from "../../../cbi-generator/generated/dto/ClassEnrollmentOverrides";
import type { ClassEnrollmentResult } from "../../../cbi-generator/generated/dto/ClassEnrollmentResult";
import { ClassEligibilityStatus } from "../../../cbi-generator/generated/dto/ClassEligibilityStatus";
import { ClassInstructorEligibilityStatus } from "../../../cbi-generator/generated/dto/ClassInstructorEligibilityStatus";
import type { GuidedSailDay } from "../../../cbi-generator/generated/dto/GuidedSailDay";
import type { ClassSessionUnified } from "../../../cbi-generator/generated/dto/ClassSessionUnified";
import type { ClassUnifiedMember } from "../../../cbi-generator/generated/dto/ClassUnifiedMember";
import type { ClassUnifiedPublic } from "../../../cbi-generator/generated/dto/ClassUnifiedPublic";
import { ProgramTypeKey } from "../../../cbi-generator/generated/dto/ProgramTypeKey";
import { SignupTypeEnum as UnifiedSignupTypeEnum } from "../../../cbi-generator/generated/dto/SignupTypeEnum";
import type { UnifiedClassSearchOptions } from "../../../cbi-generator/generated/dto/UnifiedClassSearchOptions";
import type { OrderRef } from "../../../cbi-generator/generated/dto/OrderRef";
import { staffCheckoutOrder } from "./classCheckout";

/**
 * A class as shown on the calendar, from the unified class search. Staff results are a superset
 * of this shape; Public results (no availability/eligibility) are widened to it with nulls.
 */
export type CalendarClass = ClassUnifiedMember;

export type CombinedClassType = {
  classInstance: CalendarClass;
  personEligibility: ClassEligibility[];
};

export type CalendarSession = ClassSessionUnified & { endDatetime: Date };

export type CalendarEventWithRaw = CalendarEvent & {
  rawSession: CalendarSession;
  rawClass: CalendarClass;
  rawEligibility: CombinedClassType;
};

/** Session lengths are stored in hours (see `ClassSessionUnified.sessionLength`). */
export function getSessionEnd(session: ClassSessionUnified): Date {
  return new Date(
    new Date(session.sessionDatetime).getTime() +
      (session.sessionLength ?? 0) * 60 * 60 * 1000,
  );
}

export function getCalendarSessions(
  classInstance: CalendarClass,
): CalendarSession[] {
  return (classInstance.sessions ?? [])
    .map((session) => ({ ...session, endDatetime: getSessionEnd(session) }))
    .sort(
      (a, b) =>
        new Date(a.sessionDatetime).getTime() -
        new Date(b.sessionDatetime).getTime(),
    );
}

/** The legacy `ProgramType` row id that legacy endpoints and selections still use. */
export function programTypeToLegacyId(
  programType: ProgramTypeKey,
): number | undefined {
  switch (programType) {
    case ProgramTypeKey.ADULT_PROGRAM:
      return 1;
    case ProgramTypeKey.JUNIOR_PROGRAM:
      return 2;
    default:
      return undefined;
  }
}

export function legacyIdToProgramType(
  programId: number,
): ProgramTypeKey | undefined {
  if (programId == 1) return ProgramTypeKey.ADULT_PROGRAM;
  if (programId == 2) return ProgramTypeKey.JUNIOR_PROGRAM;
  return undefined;
}

export function isSameClass(
  a: CalendarClass | undefined,
  b: CalendarClass | undefined,
) {
  return !!a && !!b && a.classId == b.classId && a.programType == b.programType;
}

function publicToCalendarClass(publicClass: ClassUnifiedPublic): CalendarClass {
  return {
    ...publicClass,
    classDescription: null,
    price: null,
    capacity: null,
    enrolledCount: null,
    waitlistedCount: null,
    instructorId: null,
    instructorName: null,
    locationId: null,
    locationName: null,
    active: true,
    adminHold: false,
    classType: null,
    availability: null,
    eligibility: null,
  };
}

function searchOptions(
  startDate: Date,
  endDate: Date,
  people: number[] = [],
): UnifiedClassSearchOptions {
  return {
    includeAvailability: true,
    includeCancelled: false,
    startDate,
    endDate,
    typeId: null,
    programType: null,
    classId: null,
    signupId: null,
    people: people.map((personId) => ({ personId })),
  };
}

export const filterTypeShoppingCart = {
  title: "Shopping Cart",
  value: "PURCHASE_HOLD",
};
export const filterTypeEnrolled = {
  title: "Enrolled",
  value: "ENROLLED",
};
export const filterTypeWaitlisted = {
  title: "Waitlisted",
  value: "WAITLISTED",
};
export const filterTypeEligible = {
  title: "Eligible",
  value: "ELIGIBLE",
};
export const filterTypeIneligible = {
  title: "Ineligible",
  value: "INELIGIBLE",
};
export const filterTypeInstructorEnrolled = {
  title: "Instructor Enrolled",
  value: "INSTRUCTOR_ENROLLED",
};
export const filterTypeInstructorEligible = {
  title: "Instructor Eligible",
  value: "INSTRUCTOR_ELIGIBLE",
};
export const filterTypeAllClasses = {
  title: "All Classes",
  value: "ALL",
};
export const filterTypeAllParticipants = {
  title: "All Participants",
  value: "ALL",
};
export const filterTypesRaw = [
  filterTypeShoppingCart,
  filterTypeEnrolled,
  filterTypeWaitlisted,
  filterTypeEligible,
  filterTypeIneligible,
  filterTypeInstructorEnrolled,
  filterTypeInstructorEligible,
];

export const allFilterTypes = filterTypesRaw.map((a) => a.value);
export const filterTypes = filterTypesRaw.concat([filterTypeAllClasses]);

export const useClassManager = (props: {
  selectablePeople?: Ref<{ title: string; value: number }[]>;
  selectedPerson?: number;
  memberOrder?: MemberOrderSession;
}) => {
  const env = useEnv();

  const notification = useNotificationStore();

  const focus = ref(new Date());

  const guidedSailTimeslots = ref<GuidedSailDay[]>([]);

  const peopleSelectedRef = ref<(number | string)[]>(
    props.selectedPerson ? [props.selectedPerson] : [],
  );

  const peopleSelected = computed<(number | string)[]>({
    get() {
      return peopleSelectedRef.value;
    },
    set(newValue) {
      if (
        !peopleSelectedRef.value.find(
          (a) => a == filterTypeAllParticipants.value,
        ) &&
        !!newValue.find((a) => a == filterTypeAllParticipants.value)
      ) {
        peopleSelectedRef.value = [filterTypeAllParticipants.value];
      } else {
        peopleSelectedRef.value = newValue.filter(
          (a) => a != filterTypeAllParticipants.value,
        );
      }
    },
  });

  const selectedPeopleIds = computed(() => {
    if (peopleSelectedRef.value.includes(filterTypeAllParticipants.value)) {
      return props.selectablePeople?.value.map((a) => a.value) || [];
    }
    return peopleSelectedRef.value as number[];
  });

  const userStore = useUser();

  const auth = useAuthStore();
  const checkoutOrder = shallowRef<OrderRef | null>(null);

  const isPreview = computed(() => !auth.isAuthenticated());

  function updateEmptyFilter() {
    if (peopleSelected.value.length == 0) {
      peopleSelected.value = [filterTypeAllParticipants.value];
    }
  }

  userStore.watchPerson((a) => {
    updateEmptyFilter();
  });
  const allClasses = ref<CombinedClassType[]>([]);

  /** [classInstance]'s eligibility entries for the currently selected people. */
  function selectedEligibility(classInstance: CombinedClassType) {
    return classInstance.personEligibility.filter(
      (eligibility) =>
        !!selectedPeopleIds.value.find(
          (personId) => personId == eligibility.personId.personId,
        ),
    );
  }
  /**
   * [classInstance]'s eligibility entries for the selectable people only (e.g. the member and their
   * children); the backend also returns other authorized people such as emergency contacts.
   */
  function selectableEligibility(classInstance: CombinedClassType) {
    const people = props.selectablePeople?.value;
    if (!people) return classInstance.personEligibility;
    return classInstance.personEligibility.filter((eligibility) =>
      people.some((person) => person.value == eligibility.personId.personId),
    );
  }
  function filterClasses() {
    if (isPreview.value) return allClasses.value;
    const filterAll = filterType.value.includes(filterTypeAllClasses.value);
    const filterShoppingCart = filterType.value.includes(
      filterTypeShoppingCart.value,
    );
    const filterEnrolled = filterType.value.includes(filterTypeEnrolled.value);
    const filterWaitlisted = filterType.value.includes(
      filterTypeWaitlisted.value,
    );
    const filterEligible = filterType.value.includes(filterTypeEligible.value);
    const filterIneligible = filterType.value.includes(
      filterTypeIneligible.value,
    );
    const filterInstructorEnrolled = filterType.value.includes(
      filterTypeInstructorEnrolled.value,
    );
    const filterInstructorEligible = filterType.value.includes(
      filterTypeInstructorEligible.value,
    );
    const filterStatus = (
      status: ClassEligibilityStatus | undefined,
      instructorStatus?: ClassInstructorEligibilityStatus | null,
    ) => {
      if (filterAll) {
        return true;
      }
      if (filterShoppingCart && status == ClassEligibilityStatus.PURCHASE_HOLD)
        return true;
      if (filterEnrolled && status == ClassEligibilityStatus.ENROLLED)
        return true;
      if (
        filterWaitlisted &&
        (status == ClassEligibilityStatus.WAITLISTED ||
          status == ClassEligibilityStatus.WAITLISTED_PENDING)
      )
        return true;
      if (filterEligible && status == ClassEligibilityStatus.ELIGIBLE)
        return true;
      if (filterIneligible && status == ClassEligibilityStatus.INELIGIBLE)
        return true;
      if (
        filterInstructorEnrolled &&
        instructorStatus == ClassInstructorEligibilityStatus.ENROLLED
      )
        return true;
      if (
        filterInstructorEligible &&
        instructorStatus == ClassInstructorEligibilityStatus.ELIGIBLE
      )
        return true;
      return false;
    };
    return allClasses.value.filter((classInstance) =>
      selectedEligibility(classInstance).some((eligibility) =>
        filterStatus(eligibility.status, eligibility.instructorStatus),
      ),
    );
  }
  const personClasses = computed(() => filterClasses());
  const loadingRef = ref(false);
  const loading = computed(() => userStore.loading.value || loadingRef.value);
  const hasSelectablePeople = computed(
    () =>
      props.selectablePeople?.value !== undefined &&
      props.selectablePeople.value.length > 0,
  );
  function getEventClasses() {
    return personClasses.value.flatMap((personClass) =>
      getCalendarSessions(personClass.classInstance).map((session) => ({
        name: getClassName(personClass.classInstance),
        color: getClassColor(personClass),
        start: new Date(session.sessionDatetime),
        end: session.endDatetime,
        timed: true,
        rawSession: session,
        rawClass: personClass.classInstance,
        rawEligibility: personClass,
      })),
    );
  }
  const events = computed<CalendarEventWithRaw[]>(() => {
    return getEventClasses(); //.concat(getEventClasses()).concat(getEventClasses()).concat(getEventClasses()).concat(getEventClasses())
  });

  function getEventsForDay(): CalendarEvent[] {
    if (!focus.value) return [];
    const dayStart = new Date(focus.value);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(focus.value);
    dayEnd.setHours(23, 59, 59, 999);

    return events.value
      .filter((e) => {
        const start = new Date(e.start);
        const end = new Date(e.end);
        return start <= dayEnd && end >= dayStart;
      })
      .sort(
        (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
      );
  }

  /**
   * Filter events for a specific hour
   */
  function getEventsForHour(hour: number): CalendarEvent[] {
    const dayEvents = getEventsForDay();
    return dayEvents.filter((e) => {
      const startHour = new Date(e.start).getHours();
      const endHour = new Date(e.end).getHours();
      return hour >= startHour && hour <= endHour;
    });
  }

  /**
   * Format the date (full weekday + month + day)
   */
  function formatDate(date: Date): string {
    return date.toLocaleDateString(undefined, {
      weekday: "long",
      month: "short",
      day: "numeric",
    });
  }

  /**
   * Format time as HH:mm
   */
  function formatTime(date: string | Date): string {
    return new Date(date).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  /**
   * Format hour label (0-23 → 12AM/1PM)
   */
  function formatHour(hour: number): string {
    const d = new Date();
    d.setHours(hour, 0, 0, 0);
    return d.toLocaleTimeString([], { hour: "2-digit", hour12: true });
  }

  const filterTypeRaw = ref([
    "PURCHASE_HOLD",
    "ENROLLED",
    "WAITLISTED",
    "ELIGIBLE",
    "INSTRUCTOR_ENROLLED",
    "INSTRUCTOR_ELIGIBLE",
  ]);
  const filterType = computed({
    get() {
      return filterTypeRaw.value;
    },
    set(newValue) {
      if (
        !filterTypeRaw.value.find((a) => a == filterTypeAllClasses.value) &&
        !!newValue.find((a) => a == filterTypeAllClasses.value)
      ) {
        filterTypeRaw.value = [filterTypeAllClasses.value];
      } else {
        filterTypeRaw.value = newValue.filter(
          (a) => a != filterTypeAllClasses.value,
        );
      }
    },
  });

  const selectablePeople = computed(() =>
    (
      (props.selectablePeople?.value as {
        title: string;
        value: string | number;
      }[]) || []
    ).concat([filterTypeAllParticipants]),
  );

  const typeToLabel = {
    month: "Month",
    week: "Week",
    day: "Day",
  };

  function addHours(date: Date, hours: number) {
    date.setHours(date.getHours() + hours);
    return date;
  }

  function getClassColor(
    personClassesEligibility: CombinedClassType,
    isSelected: boolean = false,
  ) {
    const selected = selectedEligibility(personClassesEligibility);
    const hasAnyStatus = (status: ClassEligibilityStatus) =>
      selected.some((a) => a.status == status);
    const hasAnyInstructorStatus = (status: ClassInstructorEligibilityStatus) =>
      selected.some((a) => a.instructorStatus == status);
    if (isSelected) return "blue";
    if (hasAnyInstructorStatus(ClassInstructorEligibilityStatus.ENROLLED))
      return "purple";
    if (
      hasAnyStatus(ClassEligibilityStatus.WAITLISTED) ||
      hasAnyStatus(ClassEligibilityStatus.WAITLISTED_PENDING)
    )
      return "warning";
    if (hasAnyStatus(ClassEligibilityStatus.ENROLLED)) return "green";
    if (hasAnyStatus(ClassEligibilityStatus.PURCHASE_HOLD)) return "blue";
    if (hasAnyStatus(ClassEligibilityStatus.ELIGIBLE)) return "primary";
    return "grey";
  }

  function getClassName(classInstance: CalendarClass) {
    let name = "";
    if (classInstance.weekNumber) {
      name = "Week " + classInstance.weekNumber;
    }
    return name + " " + classInstance.className + " #" + classInstance.classId;
  }

  async function searchClasses(
    startDate: Date,
    endDate: Date,
  ): Promise<CombinedClassType[]> {
    const service = useClassUnifiedService();
    const withEligibility = (classes: CalendarClass[]) =>
      classes.map((classInstance) => ({
        classInstance,
        personEligibility: classInstance.eligibility ?? [],
      }));
    if (env.mode == "STAFF") {
      const people =
        hasSelectablePeople.value && props.selectedPerson
          ? [props.selectedPerson]
          : [];
      const response = await service.classesStaff(
        searchOptions(startDate, endDate, people),
      );
      return withEligibility(response.classes);
    }
    if (auth.isAuthenticated()) {
      return withEligibility(
        await service.memberClassesMember(searchOptions(startDate, endDate)),
      );
    }
    return withEligibility(
      (
        await service.publicClassesPublic(searchOptions(startDate, endDate))
      ).map(publicToCalendarClass),
    );
  }

  const currentMonth = ref<number>();
  // The generated client takes no AbortSignal, so stale responses are dropped by token instead.
  let currentFetchToken = 0;
  async function fetchClassEnrollmentAndEligibility(
    forceLoad: boolean = false,
  ) {
    const startOfMonth = new Date(
      focus.value?.getFullYear(),
      focus.value?.getMonth(),
      1,
    );
    const month = startOfMonth.getMonth();
    if (currentMonth.value != month || forceLoad) {
      currentMonth.value = month;
      loadingRef.value = true;
      const startDate = new Date(startOfMonth);
      startDate.setMonth(startDate.getMonth() - 1);
      const endDate = new Date(startOfMonth);
      endDate.setMonth(startOfMonth.getMonth() + 1);
      const fetchToken = ++currentFetchToken;
      const guidedSailPerson = guidedSailPersonId();
      const promiseGuidedSail = guidedSailPerson
        ? fetchGuidedSailTimeslots(
            startOfMonth.getFullYear(),
            startOfMonth.getMonth() + 1, // API expects 1-based month
            guidedSailPerson,
          )
        : undefined;
      try {
        const classes = await searchClasses(startDate, endDate);

        let guidedSailData: GuidedSailDay[] = [];
        if (promiseGuidedSail) {
          try {
            guidedSailData = await promiseGuidedSail;
          } catch (error) {
            console.error("Failed to fetch guided sail timeslots:", error);
          }
        }
        if (fetchToken == currentFetchToken) {
          guidedSailTimeslots.value = guidedSailData;
        }

        if (fetchToken == currentFetchToken) {
          allClasses.value = classes;
        }
      } catch (error) {
        if (fetchToken == currentFetchToken) {
          notification.evalErrorMessage(error, "Failed to load classes");
        }
      } finally {
        if (fetchToken == currentFetchToken) {
          loadingRef.value = false;
        }
      }
    }
  }

  watch(focus, () => {
    fetchClassEnrollmentAndEligibility();
  });

  const fetch = async () => {
    await fetchClassEnrollmentAndEligibility();
  };

  onMounted(() => {
    updateEmptyFilter();
    fetch();
  });

  //fetch();

  /**
   * Member enrollment already adds the PURCHASE_HOLD to the open cart on the server; refresh the
   * cart session so the new line shows. Paid classes can't be added through the generic cart add.
   */
  async function purchaseSignup(signup: ClassEnrollmentResult): Promise<void> {
    const memberOrder = props.memberOrder;
    if (!memberOrder)
      throw new Error("Member cart is unavailable for this class enrollment.");
    if (!signup.lineItem)
      throw new Error("Enrollment did not add this class to your cart.");
    await memberOrder.refresh();
  }

  /**
   * Removes the person's PURCHASE_HOLD for this class from the shopping cart, if it is there.
   * Returns whether a cart item was removed.
   */
  async function removeHoldFromCart(
    personId: number,
    selectedClass: CalendarClass,
  ): Promise<boolean> {
    const signup = selectedClass.eligibility?.find(
      (a) => a.personId.personId == personId,
    );
    if (
      !props.memberOrder ||
      signup?.signupType != UnifiedSignupTypeEnum.PURCHASE_HOLD ||
      !signup.signupId
    )
      return false;
    const item = props.memberOrder.order.value?.lineItems?.find(
      (line) => line.classSignup?.signupId === signup.signupId,
    );
    if (!item) return false;
    await props.memberOrder.remove({ assignId: item.assignId });
    return true;
  }

  /**
   * Enrolls/waitlists/holds (or, for UNENROLLED, unenrolls) a person. Returns whether the result
   * needs payment (a PURCHASE_HOLD was added to the cart). Errors are shown as a snackbar and
   * rethrown so callers can react.
   */
  async function updateEnrollment(
    type: UnifiedSignupTypeEnum | null | undefined,
    personId: number,
    selectedClass: CalendarClass,
    overrides?: ClassEnrollmentOverrides,
  ): Promise<boolean> {
    loadingRef.value = true;
    const service = useClassUnifiedService();
    const isStaff = env.mode == "STAFF";
    const target = {
      programType: selectedClass.programType,
      classId: selectedClass.classId,
      person: { personId },
    };
    let requiresPayment = false;
    try {
      if (type == UnifiedSignupTypeEnum.UNENROLLED) {
        // Staff enrollment owns generated cart removal. Member checkout still uses the legacy cart.
        if (isStaff || !(await removeHoldFromCart(personId, selectedClass))) {
          await (isStaff
            ? service.unenrollStaff(target)
            : service.unenrollMember(target));
        }
      } else {
        const request = {
          ...target,
          order: null,
          signupType: type ?? null,
          overrides: isStaff ? (overrides ?? null) : null,
        };
        const result = await (isStaff
          ? service.enrollStaff(request)
          : service.enrollMember(request));
        if (result.signupType == UnifiedSignupTypeEnum.PURCHASE_HOLD) {
          if (isStaff) {
            checkoutOrder.value = staffCheckoutOrder(result);
          } else {
            await purchaseSignup(result);
          }
          requiresPayment = true;
        }
      }
    } catch (error: any) {
      notification.evalErrorMessage(error, "Failed to update enrollment");
      throw error;
    } finally {
      await fetchClassEnrollmentAndEligibility(true);
      loadingRef.value = false;
    }
    return requiresPayment;
  }

  function getName(personId: number) {
    return props.selectablePeople?.value.find((a) => a.value == personId)
      ?.title;
  }

  async function updateInstructorEnrollment(
    isEnroll: boolean,
    personId: number,
    selectedClass: CalendarClass,
  ): Promise<void> {
    loadingRef.value = true;
    const notification = useNotificationStore();
    const service = useClassUnifiedService();
    const isStaff = env.mode == "STAFF";
    const target = {
      programType: selectedClass.programType,
      classId: selectedClass.classId,
      person: { personId },
    };

    try {
      if (isEnroll) {
        const request = { ...target, replaceExisting: false };
        await (isStaff
          ? service.enrollInstructorStaff(request)
          : service.enrollInstructorMember(request));
        notification.addSnackbar("Enrolled as instructor", "success");
      } else {
        await (isStaff
          ? service.unenrollInstructorStaff(target)
          : service.unenrollInstructorMember(target));
        notification.addSnackbar("Unenrolled as instructor", "success");
      }
      await fetchClassEnrollmentAndEligibility(true);
    } catch (error: any) {
      notification.evalErrorMessage(
        error,
        isEnroll
          ? "Failed to enroll as instructor"
          : "Failed to unenroll as instructor",
      );
    } finally {
      loadingRef.value = false;
    }
  }

  /** The person guided sails are offered to/created for: the logged-in member, or the selected person on staff. */
  function guidedSailPersonId(): number | undefined {
    if (env.mode == "MEMBER") {
      return auth.isAuthenticated()
        ? userStore.currentPersonRef.value?.personId
        : undefined;
    }
    if (env.mode == "STAFF" && hasSelectablePeople.value) {
      return props.selectedPerson;
    }
    return undefined;
  }

  function fetchGuidedSailTimeslots(
    year: number,
    month: number,
    personId: number,
  ): Promise<GuidedSailDay[]> {
    const service = useClassUnifiedService();
    const request = { year, month, person: { personId } };
    return env.mode == "STAFF"
      ? service.guidedSailTimeslotsStaff(request)
      : service.guidedSailTimeslotsMember(request);
  }

  async function enrollGuidedSailInstructor(
    timeslot: Date,
    onCreated?: () => void,
  ): Promise<void> {
    const personId = guidedSailPersonId();
    if (!personId) {
      notification.addSnackbar("Cannot enroll: no person selected", "error");
      return;
    }
    loadingRef.value = true;
    const service = useClassUnifiedService();
    const request = { timeslot, person: { personId } };
    try {
      await (env.mode == "STAFF"
        ? service.createGuidedSailStaff(request)
        : service.createGuidedSailMember(request));
      notification.addSnackbar("Enrolled as guided sail instructor", "success");
      // Lets callers close anchored popups before the refresh re-lays out the calendar.
      onCreated?.();
      await fetchClassEnrollmentAndEligibility(true);
    } catch (error: any) {
      notification.evalErrorMessage(
        error,
        "Failed to enroll as guided sail instructor",
      );
    } finally {
      loadingRef.value = false;
    }
  }

  return {
    focus,
    filterType,
    selectablePeople,
    peopleSelected,
    peopleSelectedRef,
    selectedPeopleIds,
    selectableEligibility,
    allClasses,
    personClasses,
    loading,
    hasSelectablePeople,
    fetchClassEnrollmentAndEligibility,
    getName,
    updateEnrollment,
    checkoutOrder,
    clearCheckoutOrder: () => {
      checkoutOrder.value = null;
    },
    updateInstructorEnrollment,
    enrollGuidedSailInstructor,
    guidedSailTimeslots,
    getClassName,
    getClassColor,
  };
};

export type ClassManager = ReturnType<typeof useClassManager>;
